/**
 * Task management types
 */

import type { BaseEntity, UUID } from "./common.types";

// Task status enum
export enum TaskStatus {
  TODO = "to_do",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  BLOCKED = "blocked",
  CANCELLED = "cancelled",
}

// Task priority. The `tasks.priority` column is an INTEGER 1-5 (CHECK 1..5, default 3) in
// QA and PROD, so the UI must send numbers, never "low"/"high" strings. Higher = more urgent.
export enum TaskPriority {
  LOWEST = 1,
  LOW = 2,
  MEDIUM = 3,
  HIGH = 4,
  URGENT = 5,
}

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  [TaskPriority.LOWEST]: "Lowest",
  [TaskPriority.LOW]: "Low",
  [TaskPriority.MEDIUM]: "Medium",
  [TaskPriority.HIGH]: "High",
  [TaskPriority.URGENT]: "Urgent",
};

/** Parse a stored/selected priority; anything outside 1-5 falls back to Medium. */
export const toTaskPriority = (value: unknown): TaskPriority => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 5
    ? (n as TaskPriority)
    : TaskPriority.MEDIUM;
};

export const taskPriorityLabel = (value: unknown): string => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 5
    ? TASK_PRIORITY_LABELS[n as TaskPriority]
    : String(value ?? "");
};

// Task row: exactly the columns of public.tasks (QA and PROD, verified 2026-10-16).
// There is NO category / estimated_hours / tags / start_date / reminder column: writing one
// fails with PGRST204, so they are deliberately absent from this type.
export interface Task extends BaseEntity {
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  due_date?: string | null; // DATE (YYYY-MM-DD)
  assigned_to?: UUID | null;
  organization_id?: UUID | null;
  location_id?: UUID | null;
  event_id?: UUID | null;
  contact_id?: UUID | null;
  created_by?: UUID | null;
  completed_at?: string | null; // set by trigger set_task_completed_at
}

// Task filters for querying
export interface TaskFilters {
  status?: TaskStatus[];
  priority?: TaskPriority[];
  assigned_to?: UUID;
  created_by?: UUID;
  contact_id?: UUID;
  organization_id?: UUID;
  search?: string;
  due_before?: string;
  due_after?: string;
}
